import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import Button from './Button';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translate(-50%, -60%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 0.3s ease-out;
  
  @media (prefers-color-scheme: dark) {
    background: #2c2c2c;
    color: white;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #666;
  transition: color 0.2s ease;
  
  &:hover {
    color: #333;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.warning {
    background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
    color: #e17055;
  }
  
  &.success {
    background: linear-gradient(135deg, #81ecec, #00b894);
    color: #00a085;
  }
  
  &.danger {
    background: linear-gradient(135deg, #ff7675, #d63031);
    color: white;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ModalBody = styled.div`
  margin-bottom: 32px;
  line-height: 1.6;
  color: #666;
  
  @media (prefers-color-scheme: dark) {
    color: #ccc;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning', // warning, success, danger
  loading = false
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaCheckCircle />;
      case 'danger':
        return <FaExclamationTriangle />;
      default:
        return <FaExclamationTriangle />;
    }
  };

  const getConfirmVariant = () => {
    switch (type) {
      case 'success':
        return 'success';
      case 'danger':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        
        <ModalHeader>
          <IconWrapper className={type}>
            {getIcon()}
          </IconWrapper>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        
        <ModalBody>
          {message}
        </ModalBody>
        
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button 
            variant={getConfirmVariant()} 
            onClick={onConfirm} 
            disabled={loading}
          >
            {loading ? 'Processing...' : confirmText}
          </Button>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ConfirmationModal; 